import { useCallback, useEffect, useMemo, useState } from "react";

import AtomAssistLogo from "src/views/assistant/components/AssistantLogo";
import AAAssistantText from "src/views/assistant/components/AAAssistantText";
import AtomAssistTypingIndicator from "src/reusable/Chat/ChatMessage/AtomAssistTypingIndicator";
import ChatContainer from "src/reusable/Chat/ChatContainer";
import { ChatStreamProvider } from "src/reusable/Chat/ChatStreaming/context/ChatStreamContext";
import { ChatWrapperContainer } from "src/reusable/Chat/StyledContainers/StyledContainers";
import PartsAIChatInput from "./components/PartsAIChatInput";
import PartsAIChatInputButtons from "./components/PartsAIChatInputButtons";
import PartsAIHeader from "./components/PartsAIHeader";
import { PartsAISidebar } from "./components/sidebar/PartsAISidebar";
import { Box, Stack, useTheme } from "@mui/material";
import { usePartsAIChat } from "./hooks/usePartsAIChat";
import {
  buildMessageTree,
  getLastAssistantMessage,
} from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import {
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "src/views/assistant/stores/ChatStore";
import useLoadDialogue from "src/views/assistant/hooks/useLoadDialogue";
import useStartChatOnMount from "src/reusable/Chat/hooks/useStartChatOnMount";
import { useHistory } from "react-router-dom";
import { usePartsAIStore } from "./store/PartsAIStore";
import { PARTS_AI_PRODUCT_BY_KIND } from "./utils/chatUrl";
import type { PersistedDialogueConfig } from "src/views/products/item-equivalency/services/ItemEquivalencyService";

// Each dialogue kind has its own splash page for starting a new chat.
const NEW_CHAT_PATH_BY_DIALOGUE_KIND = {
  run: "/parts/chat",
  search: "/parts/search",
  comparison: "/parts/compare",
} as const;

const PartsAIChat = ({
  partsAIRunId,
  dialogueId,
  initialUserMessage,
}: {
  partsAIRunId: number | undefined;
  dialogueId: number | undefined;
  initialUserMessage?: string;
}) => {
  const history = useHistory();
  const theme = useTheme();
  const dialogueKind = usePartsAIStore((state) => state.dialogueKind);

  // Use ChatStore - single source of truth (matching AtomAssist pattern)
  const {
    config: chatConfig,
    setActiveDialogueId,
    setDropdownDatasets,
    setDialogue: setStoreDialogue,
    setInitialUserMessage,
  } = useChatStore();

  // Get active dataset IDs from store's computed selector
  const activeDatasetIds = useActiveDatasetIds();

  // Get source data record IDs for document-scoped search
  const sourceDataRecordIds = useSourceDataRecordIds();

  // The splash hands over one dialogue together with the scope it attached;
  // that scope must survive this component's first mount. Every other way in
  // (recents, a deep link, switching dialogues) starts from a clean scope, and
  // leaving a dialogue clears it so it cannot ride into another product. A
  // browser refresh before the first message loses a splash-attached scope:
  // nothing is stored for it until that message is sent (NuclearnAI/frontend#2848
  // moves the scope onto the dialogue so this case survives).
  const [splashDialogueId] = useState(() =>
    initialUserMessage !== undefined ? dialogueId : undefined,
  );
  // Which dialogue's stored scope has been applied; reset on leaving it so a
  // return re-hydrates from the config rather than keeping the cleared scope.
  const [hydratedScopeFor, setHydratedScopeFor] = useState<number>();
  useEffect(() => {
    if (!dialogueId) return;
    setActiveDialogueId(dialogueId);
    if (dialogueId !== splashDialogueId) {
      useChatStore.getState().setSourceDataRecordIds([]);
    }
    return () => {
      useChatStore.getState().setSourceDataRecordIds([]);
      setHydratedScopeFor(undefined);
    };
  }, [dialogueId, splashDialogueId, setActiveDialogueId]);

  // Update dropdown datasets from config
  useEffect(() => {
    if (chatConfig?.datasets) {
      setDropdownDatasets(chatConfig.datasets);
    }
  }, [chatConfig?.datasets, setDropdownDatasets]);

  // Load existing dialogue and reconstruct artifacts
  const { dialogue: loadedDialogue } = useLoadDialogue({
    initialUserMessage,
  });

  // A reopened dialogue gets its stored scope back once per visit, so a
  // later refetch cannot stomp a mid-session attachment. Dialogues with no
  // stored scope are left alone.
  useEffect(() => {
    if (
      !dialogueId ||
      loadedDialogue?.id !== dialogueId ||
      hydratedScopeFor === dialogueId
    ) {
      return;
    }
    const config = loadedDialogue.config as PersistedDialogueConfig | undefined;
    if (!config?.source_data_record_ids) return;
    setHydratedScopeFor(dialogueId);
    useChatStore
      .getState()
      .setSourceDataRecordIds(config.source_data_record_ids);
  }, [dialogueId, loadedDialogue, hydratedScopeFor]);

  // Memoize dialogue tree building
  const dialogueTree = useMemo(() => {
    if (
      loadedDialogue &&
      loadedDialogue.messages &&
      loadedDialogue.messages.length > 0
    ) {
      return buildMessageTree(loadedDialogue.messages);
    }
    return [];
  }, [loadedDialogue]);

  // Build datasets for streaming from store's computed selector (matching AtomAssist pattern)
  const datasetsToUse = useMemo(
    () => activeDatasetIds.map((id) => ({ id })),
    [activeDatasetIds],
  );

  const chatConfigMemo = useMemo(() => {
    const config = {
      dialogueId,
      modelId: chatConfig?.modelId,
      modelVersionNum: chatConfig?.modelVersionNum,
      datasetsForSearchContext: datasetsToUse,
      sourceDataRecordIds:
        sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
      numSearchResults: chatConfig?.topSearchResults || 5,
      textGenModelParams: chatConfig?.chatControlMetadata,
      systemPrompt: chatConfig?.systemPrompt,
      toolCalling: true,
      chatConfigId: chatConfig?.chatConfigId,
      // Sent every turn — prefer the dialogue's own product (a pre-split
      // search dialogue is still "parts-ai") over a value derived from route.
      product:
        loadedDialogue?.product ??
        PARTS_AI_PRODUCT_BY_KIND[dialogueKind || "run"],
    };

    return config;
  }, [
    dialogueId,
    dialogueKind,
    loadedDialogue?.product,
    chatConfig?.modelId,
    chatConfig?.modelVersionNum,
    chatConfig?.chatControlMetadata,
    chatConfig?.systemPrompt,
    chatConfig?.chatConfigId,
    chatConfig?.topSearchResults,
    datasetsToUse,
    sourceDataRecordIds,
  ]);

  const {
    dialogue,
    responseSources,
    setResponseSources,
    terminateConnection,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
    isMessageStreamStopped: isChatMessageStreamStopped,
    isTurnActive,
    emptySubmission,
    clearChatErrors,
    sendUserMessage,
    resendUserMessage,
    regenerateResponse,
    setAsPreferredMessage,
    setDialogue,
    reconstructArtifactsFromMessages,
    reset: resetChatStream,
    tokenUsage,
    setTokenUsage,
    activeSubAgents,
    store: chatStore,
  } = usePartsAIChat(chatConfigMemo, dialogueTree);

  // Load existing dialogue and reconstruct artifacts when component mounts
  // Following AtomAssist pattern - proper useEffect dependencies, React handles deduplication
  useEffect(() => {
    if (dialogueTree.length > 0) {
      setDialogue(dialogueTree);
      setStoreDialogue(dialogueTree);

      // Reconstruct artifacts from message content
      reconstructArtifactsFromMessages(dialogueTree);
    }
  }, [
    dialogueTree.length,
    dialogueId,
    setDialogue,
    setStoreDialogue,
    reconstructArtifactsFromMessages,
  ]);

  // Seed token usage from loaded dialogue config (for context window gauge on history load)
  useEffect(() => {
    if (loadedDialogue?.config?.token_usage) {
      setTokenUsage(loadedDialogue.config.token_usage);
    }
  }, [loadedDialogue?.config?.token_usage, dialogueId, setTokenUsage]);

  // Calculate last assistant message for sidebar
  const lastAssistantMessage = useMemo(
    () => getLastAssistantMessage(dialogue),
    [dialogue?.length, dialogue?.[dialogue?.length - 1]?.id],
  );

  // useStartChatOnMount sends the initial message from the splash page
  // Pass clearInitialMessage to clear the message after sending (prevents duplicate on refresh)
  useStartChatOnMount({
    initialUserMessage,
    clearInitialMessage: () => setInitialUserMessage(undefined),
    emptySubmission,
    sendUserMessage,
  });

  // A new chat redirects to the splash page for the current dialogue kind
  // (run/search/comparison) rather than always defaulting to the run splash.
  const onNewChat = useCallback(() => {
    // Clear uploaded files before navigating to splash (matching AtomAssist pattern)
    useChatStore.getState().clearUploadedFiles();
    history.push(NEW_CHAT_PATH_BY_DIALOGUE_KIND[dialogueKind || "run"]);
  }, [history, dialogueKind]);

  useEffect(() => {
    return () => {
      resetChatStream();
      terminateConnection();
      clearChatErrors();
    };
  }, [resetChatStream, terminateConnection, clearChatErrors]);

  const LoadingIndicator = useMemo(
    () => () => <AtomAssistTypingIndicator showTimer />,
    [],
  );

  return (
    <>
      <Box
        id="parts-ai-header"
        sx={{
          height: "55px",
          bgcolor: "background.paper",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
        }}
      >
        <PartsAIHeader
          dialogueId={dialogueId || null}
          onNewChat={onNewChat}
          dialogue={dialogue}
        />
      </Box>
      <ChatStreamProvider store={chatStore}>
        <Stack
          direction="row"
          sx={{
            height: "calc(100vh - 97px)",
            overflow: "hidden",
          }}
        >
          {/* Chat Pane */}
          <ChatWrapperContainer>
            <ChatContainer
              activeSubAgents={activeSubAgents}
              dialogueId={dialogueId || undefined}
              dialogue={dialogue}
              isChatConnectionError={isChatConnectionError}
              chatStreamError={chatStreamError}
              isChatConnected={isChatConnected}
              isChatEnded={isChatEnded}
              isChatLoading={isChatLoading}
              isChatMessageStreamStopped={isChatMessageStreamStopped}
              startNewChat={onNewChat}
              resendUserMessage={resendUserMessage}
              regenerateResponse={regenerateResponse}
              setAsPreferredMessage={setAsPreferredMessage}
              ChatPromptInput={
                <PartsAIChatInput
                  chatStreamError={chatStreamError}
                  terminateConnection={terminateConnection}
                  sendUserMessage={sendUserMessage}
                  isChatConnectionError={isChatConnectionError}
                  isChatLoading={isChatLoading}
                  isChatEnded={isChatEnded}
                  isTurnActive={isTurnActive}
                  InputButtons={PartsAIChatInputButtons}
                  tokenUsage={tokenUsage}
                />
              }
              BackgroundComponent={
                <AtomAssistLogo
                  lowerMessage={"What part would you like to analyze?"}
                />
              }
              CustomAssistantText={AAAssistantText}
              CustomAssistantTextProps={{
                isChatConnected,
                responseSources,
                onSourcesUpdate: setResponseSources,
              }}
              CustomLoadingIndicator={LoadingIndicator}
            />
          </ChatWrapperContainer>

          {/* Parts AI Sidebar (Sources, Artifacts, Feature Sheet) */}
          <PartsAISidebar
            responseSources={responseSources}
            lastAssistantMessage={lastAssistantMessage}
            partsAIRunId={partsAIRunId}
          />
        </Stack>
      </ChatStreamProvider>
    </>
  );
};

export default PartsAIChat;
